import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  IChartHistoryItem,
  type IClinicalChart,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  isSameRef,
  isWithRef,
  type WithRef,
} from '@principle-theorem/shared';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-charting-history-selector',
  templateUrl: './charting-history-selector.component.html',
  styleUrls: ['./charting-history-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class ChartingHistorySelectorComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  trackByItem = TrackByFunctions.label<IChartHistoryItem>();
  chartCtrl = new TypedFormControl<WithRef<IClinicalChart>>();
  chartHistory$ = new ReplaySubject<IChartHistoryItem[]>(1);

  @Output()
  selectedChange = new EventEmitter<WithRef<IClinicalChart>>();

  @Input() label = 'Select Chart';
  @Input() allowEmpty = false;

  @Input()
  set selected(selected: WithRef<IClinicalChart>) {
    if (selected) {
      this.chartCtrl.setValue(selected, { emitEvent: false });
    }
  }

  @Input()
  set chartHistory(chartHistory: IChartHistoryItem[]) {
    if (chartHistory) {
      this.chartHistory$.next(chartHistory);
    }
  }

  constructor() {
    this.chartCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((selected) => this.selectedChange.emit(selected));
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  compareFn(
    a: WithRef<IClinicalChart> | IChartHistoryItem | undefined,
    b: WithRef<IClinicalChart> | IChartHistoryItem | undefined
  ): boolean {
    const aRef = getRef(a);
    const bRef = getRef(b);
    return aRef && bRef ? isSameRef(aRef, bRef) : false;
  }
}

function getRef(
  item: WithRef<IClinicalChart> | IChartHistoryItem | undefined
): DocumentReference<IClinicalChart> | undefined {
  return isWithRef(item) ? item.ref : item?.value.ref;
}
